import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, ButtonBase, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, InputAdornment, Menu, MenuItem, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FiberNewOutlinedIcon from '@mui/icons-material/FiberNewOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import DoNotDisturbAltRoundedIcon from '@mui/icons-material/DoNotDisturbAltRounded';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { billingAdminApi } from '../services/api';
import PageHeader from '../components/common/PageHeader';
import SubTabs from '../components/common/SubTabs';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import { ErrorState } from '../components/common/States';
import { apiError, formatDate, formatDateTimeMsk, formatMoney } from '../utils/format';
import {
  PROMO_STATE, REQUEST_STATUS, formatSignedMoney, isDiscountLine, lineDetail, lineLabel, monthsLabel, requestSummary, usersLabel,
} from '../utils/tariffs';
import { tokens } from '../theme';

const TABS = [
  { key: 'requests', label: 'Заявки' },
  { key: 'promos', label: 'Промокоды' },
];

const StatusChip = ({ map, value }) => {
  const st = map[value] || { label: value, bg: tokens.primaryTint, fg: tokens.inkSoft };
  return <Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg }} />;
};

const FilterCard = ({ label, value, detail, color, icon, active, onClick }) => (
  <ButtonBase onClick={onClick} aria-pressed={active}
    sx={{ display: 'block', textAlign: 'left', p: 2.5, borderRadius: `${tokens.radius}px`, bgcolor: tokens.paper, boxShadow: tokens.shadowCard,
      border: `${active ? 2 : 1}px solid ${active ? tokens.primary : tokens.border}`, transition: 'border-color .15s, box-shadow .15s',
      '&:hover': { boxShadow: tokens.shadowRaised } }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography color="text.secondary" variant="body2">{label}</Typography>
      <Box sx={{ color, display: 'flex', opacity: 0.85 }}>{icon}</Box>
    </Stack>
    <Typography sx={{ fontSize: '2rem', fontWeight: 700, color, my: 0.5, lineHeight: 1.2 }}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{detail}</Typography>
  </ButtonBase>
);

const UserCell = ({ email, name }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, bgcolor: tokens.primarySoft, color: tokens.primaryDark }}>
      {(name || email || '?').slice(0, 1).toUpperCase()}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{email}</Typography>
      {name && <Typography variant="caption" color="text.secondary">{name}</Typography>}
    </Box>
  </Stack>
);

// ---------------------------------------------------------------- requests

const RequestDialog = ({ request, onClose, onSaved }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { setNote(request?.admin_note || ''); setError(''); }, [request]);
  if (!request) return null;

  const save = async (status) => {
    setBusy(status);
    setError('');
    try {
      const r = await billingAdminApi.setRequestStatus(request.id, status, note);
      onSaved(r.data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy('');
    }
  };
  const q = request.quote;
  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        Заявка № {request.id.slice(0, 8).toUpperCase()}
        <Typography variant="body2" color="text.secondary">от {formatDateTimeMsk(request.created_at)}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <UserCell email={request.user_email} name={request.user_name} />
          <StatusChip map={REQUEST_STATUS} value={request.status} />
        </Stack>
        <Box sx={{ p: 2, borderRadius: '12px', bgcolor: tokens.bg, border: `1px solid ${tokens.border}` }}>
          {q.lines.map((l, i) => (
            <Stack key={i} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5 }}>
              <Box>
                <Typography variant="body2" sx={{ color: isDiscountLine(l) ? tokens.success : tokens.ink }}>{lineLabel(l)}</Typography>
                <Typography variant="caption" color="text.secondary">{lineDetail(l, q.months)}</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: isDiscountLine(l) ? tokens.success : tokens.ink }}>{formatSignedMoney(l.amount)}</Typography>
            </Stack>
          ))}
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontWeight: 700 }}>Итого за {monthsLabel(request.months)}</Typography>
            <Typography sx={{ fontWeight: 700 }}>{formatMoney(request.total)}</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {usersLabel(q.users)} · {formatMoney(q.monthly_equivalent)} в месяц{request.promo_code ? ` · промокод ${request.promo_code} (−${request.promo_percent}%)` : ''}
          </Typography>
        </Box>
        <TextField label="Комментарий администратора" value={note} onChange={(e) => setNote(e.target.value)} fullWidth multiline minRows={2}
          sx={{ mt: 2.5 }} inputProps={{ maxLength: 2000 }} helperText="Например, номер счёта или договорённости с клиентом" />
        <Alert severity="info" sx={{ mt: 2 }}>
          После оплаты продлите доступ пользователю на странице <RouterLink to="/admin/users">«Пользователи»</RouterLink>.
        </Alert>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={Boolean(busy)}>Закрыть</Button>
        <Box sx={{ flex: 1 }} />
        {request.status !== 'cancelled' && (
          <Button color="error" disabled={Boolean(busy)} onClick={() => save('cancelled')}>{busy === 'cancelled' ? 'Сохранение…' : 'Отменить заявку'}</Button>
        )}
        {request.status !== 'new' && (
          <Button variant="outlined" disabled={Boolean(busy)} onClick={() => save('new')}>{busy === 'new' ? 'Сохранение…' : 'Вернуть в новые'}</Button>
        )}
        {request.status === 'processed' ? (
          <Button variant="contained" disabled={Boolean(busy)} onClick={() => save('processed')}>{busy === 'processed' ? 'Сохранение…' : 'Сохранить комментарий'}</Button>
        ) : (
          <Button variant="contained" disabled={Boolean(busy)} onClick={() => save('processed')}>{busy === 'processed' ? 'Сохранение…' : 'Отметить обработанной'}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const RequestsTab = () => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('new');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await billingAdminApi.requests();
      setItems(r.data || []);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const s = { new: 0, processed: 0, cancelled: 0, newSum: 0, processedSum: 0 };
    (items || []).forEach((r) => {
      s[r.status] += 1;
      if (r.status === 'new') s.newSum += r.total;
      if (r.status === 'processed') s.processedSum += r.total;
    });
    return s;
  }, [items]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!items) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const needle = search.trim().toLowerCase();
  const visible = items.filter((r) => (status === 'all' || r.status === status)
    && (!needle || r.user_email.toLowerCase().includes(needle) || (r.user_name || '').toLowerCase().includes(needle)));
  const cards = [
    { key: 'new', label: 'Новые', value: stats.new, detail: `На сумму ${formatMoney(stats.newSum)} — выставить счёт`, color: tokens.warning, icon: <FiberNewOutlinedIcon /> },
    { key: 'processed', label: 'Обработаны', value: stats.processed, detail: `На сумму ${formatMoney(stats.processedSum)}`, color: tokens.success, icon: <TaskAltRoundedIcon /> },
    { key: 'cancelled', label: 'Отменены', value: stats.cancelled, detail: 'Отменены или заменены новой заявкой', color: tokens.inkSoft, icon: <DoNotDisturbAltRoundedIcon /> },
    { key: 'all', label: 'Все заявки', value: items.length, detail: 'Без фильтра по статусу', color: tokens.ink, icon: <ReceiptLongOutlinedIcon /> },
  ];

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        {cards.map((c) => <FilterCard key={c.key} {...c} active={status === c.key} onClick={() => setStatus(c.key)} />)}
      </Box>
      <Card>
        <CardContent sx={{ pb: '8px !important' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <TextField placeholder="Поиск по email или имени" size="small" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220, maxWidth: 420 }}
              inputProps={{ 'aria-label': 'Поиск по email или имени' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }} />
            <Box sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">Найдено: {visible.length}</Typography>
          </Stack>
          <TableContainer sx={{ border: `1px solid ${tokens.border}`, borderRadius: '12px' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Пользователь</TableCell>
                  <TableCell>Конфигурация</TableCell>
                  <TableCell align="right">Сумма</TableCell>
                  <TableCell>Создана</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((r) => (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(r)}>
                    <TableCell><UserCell email={r.user_email} name={r.user_name} /></TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2">{requestSummary(r)}</Typography>
                      {r.admin_note && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>💬 {r.admin_note}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatMoney(r.total)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.promo_code ? `${r.promo_code} −${r.promo_percent}%` : `${formatMoney(r.quote.monthly_equivalent)}/мес`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTimeMsk(r.created_at)}</TableCell>
                    <TableCell><StatusChip map={REQUEST_STATUS} value={r.status} /></TableCell>
                    <TableCell align="right">
                      <Button size="small" variant={r.status === 'new' ? 'contained' : 'outlined'} onClick={(e) => { e.stopPropagation(); setOpen(r); }}>
                        {r.status === 'new' ? 'Обработать' : 'Открыть'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {visible.length === 0 && (
                  <TableRow><TableCell colSpan={6}>
                    <EmptyState title={items.length ? 'Заявок не найдено' : 'Заявок пока нет'}
                      description={items.length ? 'Измените фильтр или поиск.' : 'Заявки появятся, когда пользователи оформят тариф на странице «Тарифы».'}
                      action={items.length ? <Button onClick={() => { setSearch(''); setStatus('all'); }}>Сбросить фильтры</Button> : null} />
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <RequestDialog request={open} onClose={() => setOpen(null)}
        onSaved={(saved) => { setOpen(null); setItems((list) => list.map((r) => (r.id === saved.id ? saved : r))); }} />
    </>
  );
};

// ---------------------------------------------------------------- promo codes

const EMPTY_PROMO = { code: '', percent: '10', valid_until: '', max_uses: '', active: true, note: '' };

const PromoDialog = ({ open, promo, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY_PROMO);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(promo ? {
      code: promo.code, percent: String(promo.percent), valid_until: promo.valid_until || '', max_uses: promo.max_uses ? String(promo.max_uses) : '',
      active: promo.active, note: promo.note || '',
    } : EMPTY_PROMO);
  }, [open, promo]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const submit = async () => {
    const percent = Number(form.percent);
    if (!Number.isInteger(percent) || percent < 1 || percent > 90) {
      setError('Скидка — целое число от 1 до 90');
      return;
    }
    const maxUses = form.max_uses.trim() === '' ? null : Number(form.max_uses);
    if (maxUses !== null && (!Number.isInteger(maxUses) || maxUses < 1)) {
      setError('Лимит использований — целое число от 1 или пусто без лимита');
      return;
    }
    const body = { code: form.code.trim().toUpperCase(), percent, valid_until: form.valid_until || null, max_uses: maxUses, active: form.active, note: form.note };
    setBusy(true);
    setError('');
    try {
      const r = promo ? await billingAdminApi.updatePromo(promo.id, body) : await billingAdminApi.createPromo(body);
      onSaved(r.data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>{promo ? `Промокод ${promo.code}` : 'Новый промокод'}</DialogTitle>
      <DialogContent>
        <TextField label="Код" value={form.code} onChange={set('code')} fullWidth margin="normal" required disabled={Boolean(promo)}
          inputProps={{ style: { textTransform: 'uppercase' }, maxLength: 40 }}
          helperText={promo ? 'Код после создания не меняется' : 'Латинские буквы, цифры, «-» и «_». Регистр не важен — сохраняется заглавными'} />
        <TextField label="Скидка" type="number" value={form.percent} onChange={set('percent')} fullWidth margin="normal" required
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} inputProps={{ min: 1, max: 90 }}
          helperText="От 1 до 90%. Применяется после скидки за срок" />
        <TextField label="Действует до (включительно)" type="date" value={form.valid_until} onChange={set('valid_until')} fullWidth margin="normal"
          InputLabelProps={{ shrink: true }} helperText="Пусто — без срока. Дата по Москве" />
        <TextField label="Лимит использований" type="number" value={form.max_uses} onChange={set('max_uses')} fullWidth margin="normal"
          inputProps={{ min: 1 }} helperText="Сколько заявок может его использовать. Пусто — без лимита" />
        <TextField label="Комментарий" value={form.note} onChange={set('note')} fullWidth margin="normal" inputProps={{ maxLength: 500 }}
          helperText="Для себя: акция, партнёр, канал" />
        <FormControlLabel control={<Switch checked={form.active} onChange={set('active')} />} label="Промокод действует" sx={{ mt: 1 }} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Отмена</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>{busy ? 'Сохранение…' : promo ? 'Сохранить' : 'Создать'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const PromosTab = ({ createSignal }) => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [edit, setEdit] = useState(null); // { promo | null }
  const [menu, setMenu] = useState(null);
  const [remove, setRemove] = useState(null);
  const [toggling, setToggling] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await billingAdminApi.promos();
      setItems(r.data || []);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (createSignal) setEdit({ promo: null }); }, [createSignal]);

  const replace = (p) => setItems((list) => {
    const i = list.findIndex((x) => x.id === p.id);
    return i >= 0 ? list.map((x) => (x.id === p.id ? p : x)) : [p, ...list];
  });

  const toggle = async (p) => {
    setToggling(p.id);
    setNotice(null);
    try {
      const r = await billingAdminApi.updatePromo(p.id, { percent: p.percent, valid_until: p.valid_until, max_uses: p.max_uses, active: !p.active, note: p.note });
      replace(r.data);
    } catch (e) {
      setNotice({ severity: 'error', message: apiError(e) });
    } finally {
      setToggling(null);
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!items) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <>
      {notice && <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice.message}</Alert>}
      <Card>
        <CardContent sx={{ pb: '8px !important' }}>
          <TableContainer sx={{ border: `1px solid ${tokens.border}`, borderRadius: '12px' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Код</TableCell>
                  <TableCell align="right">Скидка</TableCell>
                  <TableCell>Действует до</TableCell>
                  <TableCell>Использований</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Включён</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '0.02em' }}>{p.code}</Typography>
                      {p.note && <Typography variant="caption" color="text.secondary">{p.note}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>−{p.percent}%</TableCell>
                    <TableCell>{p.valid_until ? formatDate(p.valid_until) : 'Без срока'}</TableCell>
                    <TableCell>{p.used_count}{p.max_uses ? ` из ${p.max_uses}` : ' · без лимита'}</TableCell>
                    <TableCell><StatusChip map={PROMO_STATE} value={p.state} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title={p.active ? 'Отключить промокод' : 'Включить промокод'}>
                        <span>
                          <Switch checked={p.active} disabled={toggling === p.id} onChange={() => toggle(p)} inputProps={{ 'aria-label': `Промокод ${p.code} включён` }} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton aria-label={`Действия для ${p.code}`} aria-haspopup="menu" onClick={(e) => setMenu({ anchor: e.currentTarget, promo: p })}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow><TableCell colSpan={7}>
                    <EmptyState title="Промокодов пока нет" description="Промокод даёт скидку в процентах после скидки за срок. Можно ограничить срок и число использований."
                      action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setEdit({ promo: null })}>Создать промокод</Button>} />
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { setEdit({ promo: menu.promo }); setMenu(null); }}>Изменить</MenuItem>
        <Divider />
        <MenuItem sx={{ color: 'error.main' }} onClick={() => { setRemove(menu.promo); setMenu(null); }}>Удалить</MenuItem>
      </Menu>
      <PromoDialog open={Boolean(edit)} promo={edit?.promo} onClose={() => setEdit(null)}
        onSaved={(p) => { replace(p); setEdit(null); setNotice({ severity: 'success', message: `Промокод ${p.code} сохранён.` }); }} />
      <ConfirmDialog open={Boolean(remove)} title="Удалить промокод?" danger confirmLabel="Удалить"
        text={remove ? `Промокод ${remove.code} будет удалён. Если он уже использовался в заявках, его можно только отключить.` : ''}
        onConfirm={() => billingAdminApi.deletePromo(remove.id)}
        onClose={(done) => { if (done) setItems((list) => list.filter((x) => x.id !== remove.id)); setRemove(null); }} />
    </>
  );
};

const AdminBillingPage = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'promos' ? 'promos' : 'requests';
  const [createSignal, setCreateSignal] = useState(0);
  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
      <PageHeader overline="Администрирование" title="Заявки и промокоды"
        subtitle="Заявки на тарифы с расчётом сервера и промокоды. Онлайн-оплаты нет: по заявке выставляется счёт, доступ продлевается вручную."
        actions={tab === 'promos' ? <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateSignal((n) => n + 1)}>Создать промокод</Button> : null} />
      <SubTabs items={TABS} value={tab} onChange={(k) => setParams(k === 'requests' ? {} : { tab: k }, { replace: true })} sx={{ mb: 3 }} />
      {tab === 'requests' ? <RequestsTab /> : <PromosTab createSignal={createSignal} />}
    </Box>
  );
};

export default AdminBillingPage;
